import { formatDate, formatTime } from "@clipboard-health/date-time";
import {
  BottomSheet,
  Input,
  List,
  ListItem,
  ListItemText,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { isDevelopmentNodeEnvironment } from "@src/appV2/environment";
import { type HiringReliabilityConfig } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useState } from "react";

import { type PlacementCandidate } from "../../PlacementCandidate/types";
import { type PlacementWithDetails } from "../api/useFetchPaginatedPlacements";
import { type PlacementIncludedWorkplace } from "../types/fetchPlacements.schema";

interface InterviewReliabilityBottomSheetProps {
  modalState: UseModalState;
  placementDetails: PlacementWithDetails;
  placementCandidate: PlacementCandidate;
  workplaceStartHour?: Date;
  hiringReliability: HiringReliabilityConfig;
  workplaceReliabilityBanRule?: NonNullable<
    PlacementIncludedWorkplace["meta"]
  >["reliabilityBanRule"];
  onContinue: () => void;
}

export function InterviewReliabilityBottomSheet(props: InterviewReliabilityBottomSheetProps) {
  const {
    modalState,
    placementDetails,
    placementCandidate,
    onContinue,
    hiringReliability,
    workplaceReliabilityBanRule,
    workplaceStartHour,
  } = props;

  const requiredText = "I acknowledge the attendance policy";
  const [typedText, setTypedText] = useState(isDevelopmentNodeEnvironment() ? requiredText : "");

  const { userId: workerId } = useDefinedWorker();
  const isTextCorrect = typedText.trim() === requiredText;

  const { placementInterviewAttendancePolicySupportLink } = useSupportLinks();

  const eventContext = {
    workplaceReliabilityBanRule,
    hiringReliability,
    workplaceStartHour,
    workerId,
    placementId: placementDetails.id,
    placementCandidateId: placementCandidate.id,
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_RELIABILITY_MODAL_VIEWED, eventContext);

  const handleContinue = async () => {
    if (!isTextCorrect) {
      return;
    }

    logEvent(APP_V2_APP_EVENTS.INTERVIEW_RELIABILITY_BOTTOM_SHEET_CONTINUE_CLICKED, eventContext);

    onContinue();
  };

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            disabled={!isTextCorrect}
            isLoading={false}
            onClick={handleContinue}
          >
            I understand
          </LoadingButton>
          <TextButton
            size="large"
            href={placementInterviewAttendancePolicySupportLink}
            sx={{ my: 3 }}
          >
            Learn more
          </TextButton>
        </DialogFooter>
      }
    >
      <IconButton
        size="small"
        iconType="close"
        variant="outlined"
        sx={{ position: "absolute", top: 24, right: 16 }}
        onClick={modalState.closeModal}
      />
      <Box sx={{ pt: "76px", px: 7, pb: 8 }}>
        <Stack spacing={7} alignItems="center" justifyContent="center">
          <Text variant="h3" textAlign="center">
            ⚠️ Important reminder about your interview
          </Text>

          {hiringReliability.simplifiedBanEnabled &&
          workplaceStartHour &&
          workplaceReliabilityBanRule ? (
            <>
              <Text>
                You can cancel or reschedule up until {formatTime(workplaceStartHour)} on{" "}
                {formatDate(workplaceStartHour)}. After {formatTime(workplaceStartHour)}, if you
                cancel or no-show, you&apos;ll be{" "}
                <Box
                  component="span"
                  sx={{ color: (theme) => theme.palette.error.light, fontWeight: 600 }}
                >
                  restricted from viewing jobs
                </Box>{" "}
                for {workplaceReliabilityBanRule.restrictedDays} days.
              </Text>

              <Text color={(theme) => theme.palette.text.secondary}>
                Please type:{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  “I acknowledge the attendance policy”
                </Box>{" "}
                to confirm you understand the attendance policy.
              </Text>
            </>
          ) : (
            <>
              <Stack>
                <Text textAlign="left" color={(theme) => theme.palette.text.secondary}>
                  If you no show your interview you:
                </Text>
                <List sx={{ listStyleType: "disc", pl: 5 }}>
                  <ListItem sx={{ pt: 1, pb: 0, display: "list-item" }}>
                    <ListItemText>
                      <Text color={(theme) => theme.palette.text.secondary}>
                        You could lose access to job openings for several days.
                      </Text>
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ pt: 1, pb: 0, display: "list-item" }}>
                    <ListItemText>
                      <Text color={(theme) => theme.palette.text.secondary}>
                        You could be blocked from this workplace and their partner facilities.
                      </Text>
                    </ListItemText>
                  </ListItem>
                </List>
              </Stack>

              <Text color={(theme) => theme.palette.text.secondary}>
                Please type:{" "}
                <Box component="span" fontWeight="bold">
                  “I acknowledge the attendance policy”
                </Box>{" "}
                to confirm you understand that no-shows, late cancellations, and late reschedules
                may lead to restrictions.
              </Text>
            </>
          )}

          <Input
            fullWidth
            value={typedText}
            placeholder={requiredText}
            iconType={isTextCorrect ? "checkmark" : undefined}
            onChange={(event) => {
              setTypedText(event.target.value);
            }}
          />
        </Stack>
      </Box>
    </BottomSheet>
  );
}
